import { Button, Col, Modal, Row, Tag, message } from "antd";
import { SelectOutlined } from "@ant-design/icons";
import { ProTable, ProColumns } from "@ant-design/pro-components";

import styles from "./index.less";

import { RenderInputProps } from "../types";
import { useEffect, useMemo, useState } from "react";
import { useFlow } from "../../../FlowEdit/useFlow";
import {
  DatasetStructure,
  getKnowledgeDataService,
} from "../../../../services/app";
import {
  KnowledgeBaseTypeEnum,
  KnowledgebaseDetailType,
  getKnowledgebaseListService,
} from "../../../../services/knowledgebase";
import { ResponseCode } from "../../../../../constants";

import { ReactComponent as CrossRemoveIcon } from "../../../../../assets/cross-remove.svg";
import { usePageConfig } from "../../../../PageConfigProvider";
import { useRequest } from "ahooks";

interface IKnowledgeBase {
  name: string;
  value: string;
  isDeleted: boolean;
}

type SelectKnowledgeBaseModalProps = {
  open: boolean;
  onConfirm: (value: IKnowledgeBase) => void;
  onClose: () => void;
  selected?: IKnowledgeBase;
};
const SelectKnowledgeBaseModal = ({
  onClose,
  onConfirm,
  open,
  selected,
}: SelectKnowledgeBaseModalProps) => {
  const { defaultValue } = usePageConfig();
  const appId: string = (defaultValue?.id as string) || "";
  const [selectedKnowledge, setSelectedKnowledge] = useState<IKnowledgeBase>();

  useEffect(() => {
    if (open) {
      setSelectedKnowledge(selected);
    }
  }, [open, selected]);

  const cols: ProColumns<KnowledgebaseDetailType>[] = useMemo(
    () => [
      {
        title: "知识库名称",
        dataIndex: "name",
        width: 300,
      },
      {
        title: "知识库类型",
        dataIndex: "type",
        width: 150,
        search: false,
        valueEnum: {
          [KnowledgeBaseTypeEnum.Public]: "公开",
          [KnowledgeBaseTypeEnum.Private]: "私有",
        },
        render: (text, data) => (
          <Tag
            bordered={false}
            color={
              data?.type === KnowledgeBaseTypeEnum.Public
                ? "#EFF5FF"
                : "#FFF1DE"
            }
            style={{
              color:
                data?.type === KnowledgeBaseTypeEnum.Public
                  ? "#1890FF"
                  : "#FF9C00",
            }}
          >
            {text}
          </Tag>
        ),
      },
      {
        title: "知识库描述",
        dataIndex: "description",
        width: 500,
        search: false,
      },
    ],
    []
  );

  return (
    <Modal
      title="选择知识库"
      open={open}
      footer={false}
      width={800}
      onCancel={onClose}
    >
      <div className={styles["select-container"]}>
        <div className={styles["select-knowledge-container"]}>
          <div className={styles["select-header"]}>
            {selectedKnowledge ? "已选择知识库:" : "未选择任何知识库"}
          </div>
          <div className={styles["select-knowledge"]}>
            {selectedKnowledge?.name}
          </div>
        </div>
        <div className={styles["select-actions"]}>
          <Button
            key="clear"
            type="default"
            title="清除所有的知识库选择项"
            onClick={() => {
              setSelectedKnowledge(undefined);
            }}
          >
            清除选择
          </Button>
          <Button
            key="confirm"
            type="primary"
            title="确认知识库选择"
            onClick={() => {
              if (!selectedKnowledge) {
                message.warning("请选择知识库");
                return;
              }
              if (selected && selected?.value !== selectedKnowledge.value) {
                Modal.confirm({
                  classNames: {
                    content: styles["modal-confirm-content"],
                  },
                  title:
                    "切换知识库后，将清空搜索条件和输入参数，是否确认切换?",
                  onOk() {
                    onConfirm(selectedKnowledge);
                  },
                });
              } else {
                onConfirm(selectedKnowledge);
              }
            }}
          >
            确认选择
          </Button>
        </div>
      </div>

      <div className={styles["to-select-table"]}>
        <div className={styles["table-header"]}>知识库</div>
        <ProTable
          className="pro-table"
          columns={cols}
          rowKey="id"
          rowSelection={{
            selectedRowKeys: selectedKnowledge ? [selectedKnowledge.value] : [],
            hideSelectAll: true,
            onSelect: (record, selected) => {
              if (selected) {
                setSelectedKnowledge({
                  name: record.name,
                  value: record.id,
                  isDeleted: false,
                });
              } else {
                setSelectedKnowledge(undefined);
              }
            },
          }}
          tableAlertRender={false}
          tableAlertOptionRender={false}
          toolbar={{
            settings: [],
            actions: [],
          }}
          request={async ({ current, pageSize, name }) => {
            const rsp = await getKnowledgebaseListService({
              pageSize: pageSize!,
              pageNum: current!,
              baseName: name,
              structure: DatasetStructure.Advance,
              flowId: appId,
            });

            return {
              data: rsp.data?.records,
              success: rsp.code === ResponseCode.Success,
              total: rsp.data?.total,
            };
          }}
          pagination={{ defaultPageSize: 10 }}
        />
      </div>
    </Modal>
  );
};

export default function SelectAdvanceKnowledge({
  item,
  moduleId,
}: RenderInputProps) {
  const [open, setOpen] = useState(false);
  const { updateNodeValue } = useFlow();
  const [selected, setSelected] = useState<IKnowledgeBase>();

  const { data: allKnowledges } = useRequest(async () => {
    const rsp = await getKnowledgeDataService(DatasetStructure.Advance);
    if (rsp.code === ResponseCode.Success) {
      return rsp.data as { title: string; value: string }[];
    }

    message.error("获取知识库数据失败！");
    return [];
  });

  const getInitSelectedKnowledge = useMemo(() => {
    const matched = allKnowledges?.find(
      (knowledge) => knowledge.value === item.value
    );
    if (matched) {
      return {
        name: matched?.title,
        value: matched.value,
        isDeleted: !matched?.title,
      };
    }
  }, [allKnowledges]);

  // 初始化数据
  useEffect(() => {
    setSelected(getInitSelectedKnowledge);
  }, [allKnowledges]);

  return (
    <>
      <Row gutter={[16, 8]}>
        <Col span={12}>
          <Button
            block
            type="primary"
            icon={<SelectOutlined />}
            onClick={() => {
              setOpen(true);
            }}
          >
            选择
          </Button>
        </Col>
        {selected && (
          <Col span={12} key={selected?.value}>
            <div
              className={`${
                selected.isDeleted
                  ? `${styles["knowledge-item"]} ${styles["knowledge-item-deleted"]}`
                  : `${styles["knowledge-item"]}`
              }`}
            >
              <div
                title={selected.name}
                className={styles["knowledge-item-label"]}
              >
                {selected?.name}
              </div>
              {selected.isDeleted && (
                <CrossRemoveIcon
                  title="移除该知识库"
                  className={styles["cross-remove"]}
                  onClick={() => {
                    setSelected(undefined);
                    updateNodeValue(moduleId, item.key, undefined);
                  }}
                />
              )}
            </div>
          </Col>
        )}
      </Row>
      <SelectKnowledgeBaseModal
        open={open}
        onClose={() => setOpen(false)}
        onConfirm={(value) => {
          setSelected(value);
          updateNodeValue(moduleId, item.key, value.value);
          setOpen(false);
        }}
        selected={selected}
      />
    </>
  );
}
